import { ShieldCheck } from 'lucide-react';
import type { ReactElement } from 'react';
import { Button } from '@/components/Button';
import { Notice } from '@/components/Notice';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface PlatformAdminBoardNoticeProps {
  readonly onDismiss: () => void;
}

/**
 * SA-9 — "a persistent, dismissible-per-visit banner on any board the
 * superadmin is not a member of reads 'You are viewing this board as a
 * platform admin.'"
 *
 * The component states the fact and offers the dismissal; WHEN it renders and
 * how long a dismissal lasts are `BoardFace`'s, which holds the board id the
 * dismissal is keyed by.
 *
 * ⚠ **Dismissal lives in `useState` and never in `localStorage`.** "Per visit"
 * is the requirement, and a remembered dismissal would let an administrator
 * forget, on a later visit, that they are reading someone else's board.
 *
 * `tone="info"` resolves to `role="status"`, so the sentence is announced
 * politely once and no `announce()` is added beside it — the same one-channel
 * rule `BoardClosedNotice` follows.
 */
export function PlatformAdminBoardNotice({
  onDismiss,
}: PlatformAdminBoardNoticeProps): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <Notice
      tone="info"
      icon={<ShieldCheck className="size-4" />}
      body={t('boardFace.platformAdmin.notice')}
      action={
        <Button type="button" size="sm" variant="quiet" onClick={onDismiss}>
          {t('boardFace.platformAdmin.dismiss')}
        </Button>
      }
    />
  );
}
